<template>
    <v-dialog
        :value="visible"
        :persistent="!timer"
        :max-width="dialogMaxWidth"
        :content-class="contentClass"
        transition="dialog-transition"
        @input="onInput"
    >
        <v-card class="swal-dialog-card" :class="{ 'swal-dialog-card--toast': toast }">
            <div
                v-if="!toast"
                class="swal-dialog-header"
                :class="`swal-dialog-header--${type}`"
            >
                <v-btn
                    v-if="showCloseButton"
                    icon
                    small
                    class="swal-dialog-close"
                    @click="onClose"
                >
                    <v-icon small>close</v-icon>
                </v-btn>
                <div class="swal-dialog-header__content">
                    <div
                        v-if="typeIcon"
                        class="swal-dialog-icon-badge"
                        :class="`swal-dialog-icon-badge--${type}`"
                    >
                        <v-icon :color="typeColor" :small="toast">
                            {{ typeIcon }}
                        </v-icon>
                    </div>
                    <span class="swal-dialog-title">{{ title }}</span>
                </div>
            </div>
            <v-card-title
                v-else
                class="d-flex align-center py-2"
            >
                <v-icon
                    v-if="typeIcon"
                    :color="typeColor"
                    class="mr-2"
                    left
                    small
                >
                    {{ typeIcon }}
                </v-icon>
                <span class="subtitle-1">{{ title }}</span>
                <v-spacer />
                <v-btn
                    v-if="showCloseButton"
                    icon
                    small
                    @click="onClose"
                >
                    <v-icon small>close</v-icon>
                </v-btn>
            </v-card-title>
            <v-card-text :class="toast ? 'pt-0 pb-2' : 'pt-6 pb-7 px-5'">
                <div
                    v-if="html"
                    class="swal-dialog-body"
                    v-html="html"
                />
                <div
                    v-else-if="text"
                    class="swal-dialog-body"
                >
                    {{ text }}
                </div>
                <v-select
                    v-if="input === 'select'"
                    :value="inputValue"
                    :items="selectOptions"
                    :label="inputPlaceholder"
                    outlined
                    hide-details
                    class="mt-3"
                    @input="setInputValue"
                />
                <v-textarea
                    v-else-if="input === 'textarea'"
                    :value="inputValue"
                    :placeholder="inputPlaceholder"
                    outlined
                    dense
                    rows="4"
                    hide-details
                    class="mt-3"
                    @input="setInputValue"
                />
                <v-text-field
                    v-else-if="input === 'text'"
                    :value="inputValue"
                    :placeholder="inputPlaceholder"
                    outlined
                    dense
                    hide-details
                    class="mt-3"
                    @input="setInputValue"
                />
            </v-card-text>
            <v-card-actions
                class="swal-dialog-actions"
                :class="[
                    toast
                        ? 'px-4 pt-0 pb-2 mt-0'
                        : 'px-4 pt-2 pb-3 mt-0 swal-dialog-actions--standard',
                ]"
            >
                <v-btn
                    v-if="showCancelButton"
                    class="swal-dialog-action-btn swal-dialog-action-btn--secondary"
                    outlined
                    color="grey darken-1"
                    :disabled="confirmLoading"
                    @click="onCancel"
                >
                    {{ cancelButtonText }}
                </v-btn>
                <v-btn
                    v-if="showCloseButton && !showCancelButton"
                    class="swal-dialog-action-btn swal-dialog-action-btn--secondary"
                    outlined
                    color="grey darken-1"
                    :disabled="confirmLoading"
                    @click="onClose"
                >
                    {{ closeButtonText }}
                </v-btn>
                <v-btn
                    v-if="showDenyButton"
                    class="swal-dialog-action-btn swal-dialog-action-btn--secondary"
                    outlined
                    color="grey darken-1"
                    :disabled="confirmLoading"
                    @click="onDeny"
                >
                    {{ denyButtonText }}
                </v-btn>
                <v-btn
                    class="swal-dialog-action-btn swal-dialog-action-btn--primary"
                    color="primary"
                    depressed
                    :loading="confirmLoading"
                    :disabled="confirmLoading || !validSelection"
                    @click="onConfirm"
                >
                    {{ confirmButtonText }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapState, mapActions } from "vuex";

const VALID_POSITIONS = [
    "top",
    "top-start",
    "top-end",
    "center",
    "center-start",
    "center-end",
    "bottom",
    "bottom-start",
    "bottom-end",
];

export default {
    name: "SwalDialog",
    data() {
        return {
            confirmLoading: false,
            timerId: null,
        };
    },
    computed: {
        ...mapState("swal", [
            "visible",
            "title",
            "text",
            "html",
            "type",
            "showCancelButton",
            "confirmButtonText",
            "cancelButtonText",
            "showCloseButton",
            "closeButtonText",
            "showLoaderOnConfirm",
            "showDenyButton",
            "denyButtonText",
            "timer",
            "position",
            "toast",
            "input",
            "inputPlaceholder",
            "inputValue",
            "inputOptions",
        ]),
        selectOptions() {
            return Object.entries(this.inputOptions || {}).map(([value, text]) => ({ value, text }));
        },
        validSelection() {
            return this.input !== 'select' || this.selectOptions.some(option => option.value === this.inputValue);
        },
        contentClass() {
            const pos =
                VALID_POSITIONS.indexOf(this.position) !== -1
                    ? this.position
                    : "center";
            const classes = [
                "swal-dialog-content",
                `swal-dialog-content--position_${pos.replace(/-/g, "_")}`,
            ];
            if (this.toast) {
                classes.push("swal-dialog-content--toast");
            }
            return classes.join(" ");
        },
        dialogMaxWidth() {
            if (this.toast) {
                return 360;
            }
            if (this.showDenyButton && this.showCancelButton) {
                return 760;
            }
            return 640;
        },
        typeColor() {
            const map = {
                warning: "warning",
                error: "error",
                success: "success",
                question: "primary",
                info: "info",
            };
            return map[this.type] || "info";
        },
        typeIcon() {
            const map = {
                warning: "warning",
                error: "error",
                success: "check_circle",
                question: "help_outline",
                info: "info",
            };
            return map[this.type] || "info";
        },
    },
    watch: {
        visible(val) {
            if (!val) {
                this.clearTimer();
                this.confirmLoading = false;
            } else if (this.timer != null && this.timer > 0) {
                this.startTimer();
            }
        },
        timer(val) {
            if (this.visible && val != null && val > 0) {
                this.clearTimer();
                this.startTimer();
            }
        },
    },
    beforeDestroy() {
        this.clearTimer();
    },
    methods: {
        ...mapActions("swal", ["resolve"]),
        startTimer() {
            this.clearTimer();
            this.timerId = setTimeout(() => {
                this.timerId = null;
                this.resolve({ dismiss: "timer", isDismissed: true });
            }, this.timer);
        },
        clearTimer() {
            if (this.timerId != null) {
                clearTimeout(this.timerId);
                this.timerId = null;
            }
        },
        onInput(value) {
            if (!value) {
                this.resolve({ dismiss: "close", isDismissed: true });
            }
        },
        setInputValue(val) {
            this.$store.commit("swal/SET_INPUT_VALUE", val);
        },
        onConfirm() {
            if (!this.validSelection) return;
            if (this.showLoaderOnConfirm) {
                this.confirmLoading = true;
            }
            const value = this.input ? this.inputValue : true;
            this.resolve({ value, isConfirmed: true });
        },
        onDeny() {
            this.resolve({ value: false, isDenied: true });
        },
        onCancel() {
            this.resolve({ dismiss: "cancel", isDismissed: true });
        },
        onClose() {
            this.resolve({ dismiss: "close", isDismissed: true });
        },
    },
};
</script>

<style scoped>
.swal-dialog-card {
    border-radius: 8px;
    overflow: hidden;
}
.swal-dialog-card--toast {
    min-width: 0;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
.swal-dialog-header {
    position: relative;
    padding: 12px 40px 10px 16px;
}
.swal-dialog-header__content {
    display: flex;
    align-items: center;
    gap: 12px;
}
.swal-dialog-close {
    position: absolute !important;
    top: 8px;
    right: 8px;
    z-index: 1;
}
.swal-dialog-icon-badge {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
}
.swal-dialog-icon-badge--warning {
    background: rgba(245, 124, 0, 0.14);
}
.swal-dialog-icon-badge--error {
    background: rgba(211, 47, 47, 0.12);
}
.swal-dialog-icon-badge--success {
    background: rgba(46, 125, 50, 0.12);
}
.swal-dialog-icon-badge--info {
    background: rgba(25, 118, 210, 0.12);
}
.swal-dialog-icon-badge--question {
    background: rgba(103, 58, 183, 0.12);
}
.swal-dialog-title {
    flex: 1;
    font-size: 1.0625rem;
    font-weight: 600;
    line-height: 1.3;
    color: rgba(0, 0, 0, 0.87);
}
.swal-dialog-header--warning {
    background: linear-gradient(135deg, #fff8e1 0%, #fff3e0 100%);
    border-bottom: 2px solid rgba(245, 124, 0, 0.25);
}
.swal-dialog-header--error {
    background: linear-gradient(135deg, #ffebee 0%, #fce4ec 100%);
    border-bottom: 2px solid rgba(211, 47, 47, 0.2);
}
.swal-dialog-header--success {
    background: linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%);
    border-bottom: 2px solid rgba(46, 125, 50, 0.2);
}
.swal-dialog-header--info {
    background: linear-gradient(135deg, #e3f2fd 0%, #e8eaf6 100%);
    border-bottom: 2px solid rgba(25, 118, 210, 0.2);
}
.swal-dialog-header--question {
    background: linear-gradient(135deg, #ede7f6 0%, #e8eaf6 100%);
    border-bottom: 2px solid rgba(103, 58, 183, 0.2);
}
</style>

<style lang="scss">
@import "@/assets/css/mixins.scss";

.swal-dialog-content {
    border-radius: 8px;
    margin: 12px;
}
/* Position: align dialog in the overlay (parent uses flex) */
.swal-dialog-content--position_top {
    align-self: flex-start;
    margin-bottom: auto;
}
.swal-dialog-content--position_top_start {
    align-self: flex-start;
    margin-bottom: auto;
    margin-right: auto;
}
.swal-dialog-content--position_top_end {
    align-self: flex-start;
    margin-bottom: auto;
    margin-left: auto;
}
.swal-dialog-content--position_center {
    /* default centered */
}
.swal-dialog-content--position_center_start {
    margin-right: auto;
}
.swal-dialog-content--position_center_end {
    margin-left: auto;
}
.swal-dialog-content--position_bottom {
    align-self: flex-end;
    margin-top: auto;
}
.swal-dialog-content--position_bottom_start {
    align-self: flex-end;
    margin-top: auto;
    margin-right: auto;
}
.swal-dialog-content--position_bottom_end {
    align-self: flex-end;
    margin-top: auto;
    margin-left: auto;
}
.swal-dialog-content--toast {
    max-width: 360px;
    margin: 12px;
}
.swal-dialog-body {
    line-height: 1.55;
    color: rgba(0, 0, 0, 0.7);
    font-size: 0.9375rem;
}
.swal-dialog-body p {
    margin-bottom: 0.75rem;
}
.swal-dialog-body strong {
    color: rgba(0, 0, 0, 0.87);
    font-weight: 600;
}
.swal-dialog-body ul {
    padding-left: 1.25rem;
}
.swal-dialog-body .swal-dialog-scope-list {
    margin: 0.75rem 0 2.25rem;
    padding: 10px 14px 10px 2rem;
    background: #fff8e1;
    border: 1px solid rgba(245, 124, 0, 0.22);
    border-radius: 6px;
    list-style: disc;
}
.swal-dialog-body .swal-dialog-scope-list li {
    margin-bottom: 4px;
    color: rgba(0, 0, 0, 0.8);
    font-weight: 500;
}
.swal-dialog-body .swal-dialog-scope-list li:last-child {
    margin-bottom: 0;
}
.swal-dialog-body .swal-dialog-scope-footer p:last-child {
    margin-bottom: 0;
}

/* Actions: keep standard dialogs on one row; wrap only on very small viewports */
.swal-dialog-actions--standard {
    background: #fafafa;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    flex-wrap: nowrap !important;
}
.swal-dialog-actions {
    display: flex !important;
    flex-wrap: wrap !important;
    justify-content: flex-end !important;
    align-items: center;
    gap: 8px;
    width: 100%;
    box-sizing: border-box;
}

.swal-dialog-actions--standard .swal-dialog-action-btn {
    flex: 0 0 auto;
    white-space: nowrap !important;
    text-transform: none !important;
    font-size: 0.875rem !important;
    letter-spacing: 0.01em !important;
    padding-left: 14px !important;
    padding-right: 14px !important;
}

.swal-dialog-action-btn {
    white-space: normal !important;
    height: auto !important;
    min-height: 32px;
    padding-top: 6px !important;
    padding-bottom: 6px !important;
    max-width: 100%;
    font-weight: 600;
    letter-spacing: 0.02em;
}

.swal-dialog-action-btn--secondary.v-btn--outlined {
    background-color: #fff !important;
    border-color: rgba(0, 0, 0, 0.28) !important;
}

.swal-dialog-action-btn--primary.v-btn {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12) !important;
}

@include respond-to(xs) {
    .swal-dialog-actions {
        flex-direction: column !important;
        align-items: stretch !important;
        justify-content: stretch !important;
    }

    .swal-dialog-action-btn.v-btn {
        width: 100%;
        max-width: 100%;
        margin-left: 0 !important;
        margin-right: 0 !important;
    }
}
</style>
